/**
 * <vsr-header> — sticky state, dropdown enhancement and mobile drawer.
 *
 * Progressive enhancement only. Before this file runs:
 *   - every navigation link is a real <a> in the DOM;
 *   - every dropdown is a native <details>/<summary>, so it opens by click and
 *     by keyboard with no JavaScript at all;
 *   - each localization <select> (country/region, language) submits through
 *     its own form's submit button.
 *
 * What this element adds:
 *   - Escape closes the open dropdown and returns focus to its trigger;
 *   - Arrow keys / Home / End move between items inside an open dropdown;
 *   - only one dropdown is open at a time, and clicking or tabbing away closes it;
 *   - hover opens a dropdown for mouse users (never for touch);
 *   - a focus-trapped, scroll-locked, full-height mobile drawer;
 *   - the scrolled header state that turns on the hairline and the blur;
 *   - a localization <select> submits as soon as a pointer picks an option,
 *     and on Enter after a keyboard change.
 *
 * All listeners are bound with an AbortController signal so that
 * disconnectedCallback() — which the Theme Editor triggers constantly — leaves
 * nothing behind.
 */

(function () {
  'use strict';

  const SCROLL_THRESHOLD = 8;
  const HOVER_CLOSE_DELAY = 140;
  const FINE_POINTER = '(hover: hover) and (pointer: fine)';
  // The theme's own page at <body> level: the section-group wrappers Shopify
  // renders, plus the skip link and <main> (layout/theme.liquid).
  const PAGE_REGION = '.shopify-section, [data-page-region]';

  class VsrHeader extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.header = this.querySelector('[data-header]');
      this.toggle = this.querySelector('[data-drawer-toggle]');
      this.drawer = this.querySelector('[data-drawer]');
      this.drawerClose = this.querySelector('[data-drawer-close]');
      this.menus = Array.from(this.querySelectorAll('[data-menu]'));
      this.focusTrap = null;
      this.focusFrame = 0;
      this.backgroundElements = [];
      this.hoverTimer = 0;

      this.desktopQuery = window.VSR?.mediaUp('lg') ?? window.matchMedia('(min-width: 990px)');
      this.pointerQuery = window.matchMedia(FINE_POINTER);

      this.setupMenus(signal);
      this.setupDrawer(signal);
      this.setupLocaleForms(signal);
      this.setupSticky(signal);

      document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal });

      this.desktopQuery.addEventListener(
        'change',
        (event) => {
          if (event.matches) this.closeDrawer();
          else this.closeMenus();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      window.clearTimeout(this.hoverTimer);
      this.closeDrawer();
      this.controller?.abort();
    }

    /* ---- Dropdowns ---------------------------------------------------------- */

    setupMenus(signal) {
      if (this.menus.length === 0) return;

      this.menus.forEach((menu) => {
        const summary = menu.querySelector('summary');
        const panel = menu.querySelector('[data-menu-panel]');
        if (!summary || !panel) return;

        // Native <details> can be opened by anything; keep siblings closed.
        menu.addEventListener(
          'toggle',
          () => {
            if (menu.open) this.closeMenus(menu);
          },
          { signal }
        );

        summary.addEventListener('keydown', (event) => this.onSummaryKeydown(event, menu), {
          signal
        });

        // A menu already opened by hover must not close on the click the user
        // makes to "open" it — swallow exactly that one click.
        summary.addEventListener(
          'click',
          (event) => {
            if (menu.dataset.hoverOpened !== 'true') return;
            delete menu.dataset.hoverOpened;
            event.preventDefault();
          },
          { signal }
        );
        panel.addEventListener('keydown', (event) => this.onPanelKeydown(event, menu), { signal });

        // Hover is an enhancement for mouse users only. Touch and pen still need
        // the click, which the native disclosure already handles.
        menu.addEventListener(
          'pointerenter',
          (event) => {
            if (!this.canHover(event)) return;
            window.clearTimeout(this.hoverTimer);
            if (!menu.open) menu.dataset.hoverOpened = 'true';
            menu.open = true;
          },
          { signal }
        );

        menu.addEventListener(
          'pointerleave',
          (event) => {
            if (!this.canHover(event)) return;
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = window.setTimeout(() => {
              if (menu.contains(document.activeElement)) return;
              delete menu.dataset.hoverOpened;
              menu.open = false;
            }, HOVER_CLOSE_DELAY);
          },
          { signal }
        );
      });

      const closeOutsideMenu = (event) => {
        const activeMenu = this.menus.find((menu) => menu.contains(event.target));
        this.closeMenus(activeMenu);
      };
      document.addEventListener('pointerdown', closeOutsideMenu, { signal });
      document.addEventListener('focusin', closeOutsideMenu, { signal });
    }

    canHover(event) {
      return (
        event.pointerType === 'mouse' && this.pointerQuery.matches && this.desktopQuery.matches
      );
    }

    menuLinks(menu) {
      return Array.from(menu.querySelectorAll('[data-menu-panel] a[href]'));
    }

    onSummaryKeydown(event, menu) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        menu.open = true;
        this.menuLinks(menu)[0]?.focus();
        return;
      }

      if (event.key === 'Escape' && menu.open) {
        event.preventDefault();
        this.closeMenu(menu, true);
      }
    }

    onPanelKeydown(event, menu) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeMenu(menu, true);
        return;
      }

      const links = this.menuLinks(menu);
      if (links.length === 0) return;

      const current = links.indexOf(document.activeElement);
      const from = current < 0 ? 0 : current;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        links[(from + 1) % links.length].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        links[(from - 1 + links.length) % links.length].focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        links[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        links[links.length - 1].focus();
      }
    }

    closeMenu(menu, restoreFocus) {
      delete menu.dataset.hoverOpened;
      if (!menu.open) return;
      menu.open = false;
      if (restoreFocus) menu.querySelector('summary')?.focus();
    }

    closeMenus(except) {
      this.menus.forEach((menu) => {
        if (menu !== except) this.closeMenu(menu, false);
      });
    }

    /* ---- Drawer -------------------------------------------------------------- */

    setupDrawer(signal) {
      if (!this.toggle || !this.drawer) return;

      this.toggle.addEventListener('click', () => this.toggleDrawer(), { signal });
      this.drawerClose?.addEventListener('click', () => this.closeDrawer(), { signal });

      // Following a link inside the drawer should not leave it open behind the
      // next page paint, nor on same-page anchors.
      this.drawer.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('a[href]')) this.closeDrawer();
        },
        { signal }
      );
    }

    get isDrawerOpen() {
      return this.drawer?.dataset.open === 'true';
    }

    toggleDrawer() {
      if (this.isDrawerOpen) this.closeDrawer();
      else this.openDrawer();
    }

    setDrawerState(open) {
      this.drawer.dataset.open = open ? 'true' : 'false';
      this.drawer.inert = !open;
      this.drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      const label = open ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen;
      if (label) this.toggle.setAttribute('aria-label', label);
    }

    openDrawer() {
      if (!this.drawer || !this.toggle || this.isDrawerOpen || this.desktopQuery.matches) return;
      this.closeMenus();
      this.setDrawerState(true);
      window.VSR?.scrollLock.lock();
      // trapFocus records the currently focused element (the toggle) and returns
      // focus to it on release, which is what makes Escape land back on the
      // control that opened the drawer.
      this.focusTrap = window.VSR?.trapFocus(this.drawer) ?? null;
      this.isolateDrawer();
      this.focusFrame = window.requestAnimationFrame(() => {
        this.focusFrame = 0;
        if (!this.isConnected || !this.isDrawerOpen) return;
        const first = this.drawerClose ?? this.drawer.querySelector('a[href]');
        first?.focus({ preventScroll: true });
      });
    }

    closeDrawer() {
      window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = 0;
      if (!this.isDrawerOpen) return;
      this.backgroundElements.forEach(([element, inert]) => {
        element.inert = inert;
      });
      this.backgroundElements = [];
      this.focusTrap?.release();
      this.focusTrap = null;
      const returnTarget = this.desktopQuery.matches
        ? this.header?.querySelector('a[href]')
        : this.toggle;
      returnTarget?.focus({ preventScroll: true });
      this.setDrawerState(false);
      window.VSR?.scrollLock.unlock();
    }

    // At <body> level only the theme's own page goes inert, never what the
    // platform puts beside it. Shopify inserts its cookie banner as body's
    // first child, painted above the drawer; made inert, it stayed on screen
    // looking live while every tap on Accept fell through to the drawer.
    isolateDrawer() {
      let branch = this.drawer;
      while (branch?.parentElement) {
        const parent = branch.parentElement;
        const atBody = parent === document.body;
        Array.from(parent.children).forEach((element) => {
          if (element === branch || !(element instanceof HTMLElement)) return;
          if (atBody && !element.matches(PAGE_REGION)) return;
          this.backgroundElements.push([element, element.inert]);
          element.inert = true;
        });
        if (atBody) break;
        branch = parent;
      }
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;

      if (this.isDrawerOpen) {
        this.closeDrawer();
        return;
      }

      const open = this.menus.find((menu) => menu.open);
      if (open) this.closeMenu(open, true);
    }

    /* ---- Localization selects (country/region, language) --------------------- */

    // A pointer choice submits at once: the visitor opened the list and picked.
    // A keyboard change does not. Arrowing or typing on a closed <select> fires
    // `change` on every key in Chrome and Firefox, and reloading the page — in
    // another market and currency — on the first key is a change of context
    // nobody asked for (WCAG 3.2.2). Keyboard users commit with Enter, or with
    // the form's submit button, which surfaces on focus.
    setupLocaleForms(signal) {
      this.querySelectorAll('[data-locale-select]').forEach((select) => {
        const form = select.form;
        if (!form) return;

        const rendered = Array.from(select.options).find((option) => option.defaultSelected);
        const current = rendered ? rendered.value : select.value;
        let byPointer = false;

        const submit = () => {
          if (select.value === current) return;
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        };

        select.addEventListener(
          'pointerdown',
          () => {
            byPointer = true;
          },
          { signal }
        );
        select.addEventListener(
          'keydown',
          (event) => {
            byPointer = false;
            // Deferred, so a choice Enter commits inside an open list lands first.
            if (event.key === 'Enter') window.setTimeout(submit, 0);
          },
          { signal }
        );
        select.addEventListener(
          'change',
          () => {
            if (byPointer) submit();
            byPointer = false;
          },
          { signal }
        );
      });
    }

    /* ---- Sticky / scrolled state --------------------------------------------- */

    setupSticky(signal) {
      if (!this.hasAttribute('sticky') || !this.header) return;

      const update = (window.VSR?.rafThrottle ?? ((fn) => fn))(() => {
        this.header.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
      });

      update();
      window.addEventListener('scroll', update, { passive: true, signal });
    }
  }

  if (window.VSR) window.VSR.define('vsr-header', VsrHeader);
  else if (!customElements.get('vsr-header')) customElements.define('vsr-header', VsrHeader);
})();
